import { RefreshCw } from "lucide-react";
import { slugify } from "../../../lib/format";

/* Step 7 — SEO. Slug auto-suggests from the title (one-click refresh
   button). A search-result preview at the bottom shows roughly how the
   listing would look in Google. */
export function SeoStep({ data, setData }) {
  const seo = data.seo || { metaTitle: "", metaDescription: "", focusKeyword: "" };
  const setSeo = (patch) => setData({ ...data, seo: { ...seo, ...patch } });
  const setSlug = (v) => setData({ ...data, slug: v });

  const suggestSlug = () => setSlug(slugify(data.title));

  const fieldCls = "w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500";
  const labelCls = "text-xs font-medium text-slate-600 mb-1.5 block";

  return (
    <div className="space-y-5">
      <div>
        <label className={labelCls}>URL slug</label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 select-none">abhighar.com/properties/</span>
          <input
            value={data.slug}
            onChange={(e) => setSlug(slugify(e.target.value))}
            placeholder="godrej-hill-retreat-mahalunge"
            className={fieldCls + " flex-1 font-mono text-xs"}
          />
          <button
            onClick={suggestSlug}
            className="text-xs font-medium text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 px-2 py-2 rounded-lg hover:bg-blue-50"
            title="Generate from title"
          >
            <RefreshCw className="w-3.5 h-3.5" />Suggest
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">
          Lowercase, hyphen-separated, ASCII only. Keep it short and descriptive &mdash; it&apos;s the URL search engines and shared links use.
        </p>
      </div>

      <div>
        <label className={labelCls}>Focus keyword</label>
        <input
          value={seo.focusKeyword}
          onChange={(e) => setSeo({ focusKeyword: e.target.value })}
          placeholder="e.g. 2 BHK in Mahalunge"
          className={fieldCls + " max-w-md"}
        />
        <p className="text-[10px] text-slate-400 mt-1">
          The primary search phrase you want this page to rank for. Used as a self-check &mdash; aim to include it naturally in the title and description.
        </p>
      </div>

      <div>
        <label className={labelCls}>Meta title</label>
        <input
          value={seo.metaTitle}
          onChange={(e) => setSeo({ metaTitle: e.target.value })}
          placeholder="Auto-generated from project title if left blank"
          className={fieldCls}
          maxLength={70}
        />
        <p className="text-[10px] text-slate-400 mt-1 tabular-nums">{(seo.metaTitle || "").length} / 60 characters &middot; Google truncates around 60.</p>
      </div>

      <div>
        <label className={labelCls}>Meta description</label>
        <textarea
          value={seo.metaDescription}
          onChange={(e) => setSeo({ metaDescription: e.target.value })}
          rows={3}
          placeholder="Concise summary of the project shown in search results."
          className={fieldCls + " resize-y"}
          maxLength={180}
        />
        <p className="text-[10px] text-slate-400 mt-1 tabular-nums">{(seo.metaDescription || "").length} / 160 characters &middot; Google truncates around 160.</p>
      </div>

      {/* Google SERP-style preview — gives an honest sense of what the
          listing will look like in search results. */}
      <div className="pt-4 border-t border-slate-200">
        <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Search result preview
        </div>
        <div className="bg-white rounded-xl ring-1 ring-slate-200 p-4 max-w-2xl">
          <div className="flex items-center gap-2 text-xs text-slate-600 mb-1">
            <span className="w-5 h-5 rounded-full bg-slate-100 grid place-items-center text-[10px] font-bold text-slate-500">AG</span>
            <div>
              <div className="font-medium">Abhi Ghar</div>
              <div className="text-[11px] text-slate-400 truncate">
                abhighar.com &rsaquo; properties &rsaquo; {data.slug || "your-slug"}
              </div>
            </div>
          </div>
          <div className="text-blue-800 text-lg leading-tight mt-1 hover:underline cursor-default truncate">
            {seo.metaTitle || data.title || "Your project title"}
          </div>
          <div className="text-sm text-slate-600 mt-1 leading-snug line-clamp-2">
            {seo.metaDescription || data.shortDescription || "Add a meta description above to control how this listing appears in search results."}
          </div>
        </div>
      </div>
    </div>
  );
}
